import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { ExperienceOffer, getExperienceOffers } from '~/services/ExperiencesService';
import SearchService from '~/services/SearchService';

interface UseExperienceOffersByPlaceIdsParams {
  placeIds: Array<string>;
  enabled?: boolean;
}

const STALE_TIME = 5 * 60 * 1000;

export interface ExperienceOfferItem {
  id: string;
  title: string;
  imageId?: string;
}

export function useExperienceOffersByPlaceIds({ placeIds, enabled = true }: UseExperienceOffersByPlaceIdsParams) {
  const brand = useSelector((state: App.State) => state.tenant.brand);

  const placesQueries = useQueries({
    queries: placeIds.map((placeId) => ({
      queryKey: ['destinations', 'place', placeId],
      queryFn: async () => {
        const result = await SearchService.getPlace(placeId);
        return {
          placeId,
          result,
        };
      },
      enabled: enabled && !!placeId,
      staleTime: STALE_TIME,
    })),
  });

  const coordinatesByPlaceId = useMemo(() => {
    const coordsMap = new Map<string, { lat: number; lng: number }>();
    placesQueries.forEach((query) => {
      if (query.data?.result) {
        const place = query.data.result?.result;
        if (place?.centroid?.coordinates && Array.isArray(place.centroid.coordinates)) {
          coordsMap.set(query.data.placeId, {
            lat: place.centroid.coordinates[1],
            lng: place.centroid.coordinates[0],
          });
        }
      }
    });
    return coordsMap;
  }, [placesQueries]);

  const experienceOffersQueries = useQueries({
    queries: Array.from(coordinatesByPlaceId.entries()).map(([placeId, coordinates]) => ({
      queryKey: ['experience-offers', 'by-coordinates', placeId, coordinates.lat, coordinates.lng, brand],
      queryFn: async () => {
        const response = await getExperienceOffers({
          curationData: true,
          limit: 1000,
          filters: {
            status: ['APPROVED'],
            distance: `629km`,
            coordinates: {
              lat: coordinates.lat,
              lng: coordinates.lng,
            },
          },
          brand: brand.toLowerCase(),
          sort: 'SCORE',
          sortOrientation: 'DESC',
        });
        return {
          placeId,
          offers: response.result || [],
        };
      },
      enabled: enabled && !!placeId && !!coordinates,
      staleTime: STALE_TIME,
    })),
  });

  const allExperienceOfferIds = useMemo(() => {
    const offerIdSet = new Set<string>();
    experienceOffersQueries.forEach((query) => {
      if (query.data?.offers) {
        query.data.offers.forEach((offer: ExperienceOffer) => {
          offerIdSet.add(offer.id);
        });
      }
    });
    return Array.from(offerIdSet);
  }, [experienceOffersQueries]);

  const offerIdsByPlaceId = useMemo(() => {
    const mapping: Record<string, Array<string>> = {};
    experienceOffersQueries.forEach((query) => {
      if (query.data?.placeId && query.data?.offers) {
        mapping[query.data.placeId] = query.data.offers.map((offer: ExperienceOffer) => offer.id);
      }
    });
    return mapping;
  }, [experienceOffersQueries]);

  const offers = useMemo(() => {
    const offerMap = new Map<string, ExperienceOfferItem>();
    experienceOffersQueries.forEach((query) => {
      if (query.data?.offers) {
        query.data.offers.forEach((offer: ExperienceOffer) => {
          if (!offerMap.has(offer.id)) {
            offerMap.set(offer.id, {
              id: offer.id,
              title: offer.title,
              imageId: offer.images?.[0]?.id,
            });
          }
        });
      }
    });
    return Array.from(offerMap.values());
  }, [experienceOffersQueries]);

  const isLoading =
    placesQueries.some((query) => query.isLoading) || experienceOffersQueries.some((query) => query.isLoading);
  const isError =
    placesQueries.some((query) => query.isError) || experienceOffersQueries.some((query) => query.isError);

  return {
    offers,
    isLoading,
    isError,
    allExperienceOfferIds,
    offerIdsByPlaceId,
  };
}
